/**
 * Canvas Dropzone Hook
 * Handles drag & drop for images, Lottie, Rive, and .slate files
 * Also handles paste for text and images
 */

import { useCallback, useEffect, useState } from 'react';
import { nanoid } from 'nanoid';
import { useTemplateStore } from '@/stores/designStore';
import { useToolbarStore } from '@/stores/toolbarStore';
import { createAbsolutePosition } from '@/types/position';
import type { ImageLayer, VideoLayer, AudioLayer, LottieLayer, RiveLayer, TextLayer } from '@/types/layers';
import { createAlignmentPosition } from '@/types/position';

interface UseCanvasDropzoneOptions {
  enabled?: boolean;
  canvasWidth: number;
  canvasHeight: number;
}

export type DraggedFileType = 'image' | 'video' | 'audio' | 'lottie' | 'rive' | 'slate' | 'unknown' | null;

interface DropzoneState {
  isDraggingOver: boolean;
  draggedFileType: DraggedFileType;
}

// Supported file extensions
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.aac', '.m4a', '.flac'];
const LOTTIE_EXTENSIONS = ['.json', '.lottie'];
const RIVE_EXTENSIONS = ['.riv'];
const SLATE_EXTENSIONS = ['.slate'];

function getFileType(file: File): Exclude<DraggedFileType, null> {
  const name = file.name.toLowerCase();
  const mimeType = file.type;

  // Check by extension
  if (IMAGE_EXTENSIONS.some(ext => name.endsWith(ext))) return 'image';
  if (VIDEO_EXTENSIONS.some(ext => name.endsWith(ext))) return 'video';
  if (AUDIO_EXTENSIONS.some(ext => name.endsWith(ext))) return 'audio';
  if (LOTTIE_EXTENSIONS.some(ext => name.endsWith(ext))) return 'lottie';
  if (RIVE_EXTENSIONS.some(ext => name.endsWith(ext))) return 'rive';
  if (SLATE_EXTENSIONS.some(ext => name.endsWith(ext))) return 'slate';

  // Check by mime type
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/json') return 'lottie'; // Assume JSON is Lottie

  return 'unknown';
}

export function useCanvasDropzone({
  enabled = true,
  canvasWidth,
  canvasHeight,
}: UseCanvasDropzoneOptions) {
  const [dropzoneState, setDropzoneState] = useState<DropzoneState>({
    isDraggingOver: false,
    draggedFileType: null,
  });

  const { addLayer, selectLayer, loadTemplate } = useTemplateStore();
  const { textDefaults } = useToolbarStore();

  // Read file as data URL
  const readFileAsDataURL = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Read file as text
  const readFileAsText = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }, []);

  // Create image layer from file
  const createImageFromFile = useCallback(async (file: File, x: number, y: number) => {
    const dataUrl = await readFileAsDataURL(file);

    // Get image dimensions
    const img = new Image();
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.src = dataUrl;
    });

    // Scale down if too large
    let width = img.width;
    let height = img.height;
    const maxSize = 400;
    if (width > maxSize || height > maxSize) {
      const scale = maxSize / Math.max(width, height);
      width *= scale;
      height *= scale;
    }

    const layer: ImageLayer = {
      id: nanoid(),
      type: 'image',
      name: file.name.split('.')[0] || 'Image',
      position: createAbsolutePosition(x - width / 2, y - height / 2),
      width,
      height,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        src: dataUrl,
        objectFit: 'cover',
      },
    };

    addLayer(layer);
    selectLayer(layer.id);
  }, [readFileAsDataURL, addLayer, selectLayer]);

  // Create video layer from file
  const createVideoFromFile = useCallback(async (file: File, x: number, y: number) => {
    const dataUrl = await readFileAsDataURL(file);

    // Get video dimensions
    const video = document.createElement('video');
    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => resolve();
      video.src = dataUrl;
    });

    // Scale down if too large
    let width = video.videoWidth;
    let height = video.videoHeight;
    const maxSize = 400;
    if (width > maxSize || height > maxSize) {
      const scale = maxSize / Math.max(width, height);
      width *= scale;
      height *= scale;
    }

    const layer: VideoLayer = {
      id: nanoid(),
      type: 'video',
      name: file.name.split('.')[0] || 'Video',
      position: createAbsolutePosition(x - width / 2, y - height / 2),
      width,
      height,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        src: dataUrl,
        objectFit: 'cover',
        loop: true,
        muted: true,
        autoplay: true,
      },
    };

    addLayer(layer);
    selectLayer(layer.id);
  }, [readFileAsDataURL, addLayer, selectLayer]);

  // Create audio layer from file
  const createAudioFromFile = useCallback(async (file: File) => {
    const dataUrl = await readFileAsDataURL(file);

    // Get audio duration
    const audio = new Audio();
    let duration = 0;
    await new Promise<void>((resolve) => {
      audio.onloadedmetadata = () => {
        duration = audio.duration;
        resolve();
      };
      audio.onerror = () => resolve();
      audio.src = dataUrl;
    });

    const layer: AudioLayer = {
      id: nanoid(),
      type: 'audio',
      name: file.name.split('.')[0] || 'Audio',
      position: createAlignmentPosition('Center'), // Audio has no visual position
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        src: dataUrl,
        name: file.name,
        loop: false,
        volume: 1,
        duration,
      },
    };

    addLayer(layer);
    selectLayer(layer.id);
  }, [readFileAsDataURL, addLayer, selectLayer]);

  // Create Lottie layer from file
  const createLottieFromFile = useCallback(async (file: File, x: number, y: number) => {
    let src: string;
    let data: object | undefined;

    if (file.name.endsWith('.lottie')) {
      // .lottie files are loaded by URL (we'll use data URL)
      src = await readFileAsDataURL(file);
    } else {
      // .json files - parse and inline the data
      const text = await readFileAsText(file);
      try {
        data = JSON.parse(text);
        src = ''; // Will use data instead
      } catch {
        console.error('Failed to parse Lottie JSON');
        return;
      }
    }

    const size = 200; // Default size for Lottie
    const layer: LottieLayer = {
      id: nanoid(),
      type: 'lottie',
      name: file.name.split('.')[0] || 'Animation',
      position: createAbsolutePosition(x - size / 2, y - size / 2),
      width: size,
      height: size,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        src: src || undefined,
        data,
        autoplay: true,
        loop: true,
        speed: 1,
        direction: 1,
        renderer: 'svg',
      },
    };

    addLayer(layer);
    selectLayer(layer.id);
  }, [readFileAsDataURL, readFileAsText, addLayer, selectLayer]);

  // Create Rive layer from file
  const createRiveFromFile = useCallback(async (file: File, x: number, y: number) => {
    // Rive needs to be loaded from a URL, so we use object URL
    const objectUrl = URL.createObjectURL(file);

    const size = 200; // Default size for Rive
    const layer: RiveLayer = {
      id: nanoid(),
      type: 'rive',
      name: file.name.split('.')[0] || 'Rive Animation',
      position: createAbsolutePosition(x - size / 2, y - size / 2),
      width: size,
      height: size,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        src: objectUrl,
        autoplay: true,
        fit: 'contain',
        alignment: 'center',
      },
    };

    addLayer(layer);
    selectLayer(layer.id);
  }, [addLayer, selectLayer]);

  // Load .slate template file
  const loadSlateFile = useCallback(async (file: File) => {
    try {
      const text = await readFileAsText(file);
      const template = JSON.parse(text);

      // Validate it looks like a template
      if (template.metadata && template.canvas && template.layers) {
        loadTemplate(template);
      } else {
        console.error('Invalid .slate file format');
      }
    } catch (error) {
      console.error('Failed to load .slate file:', error);
    }
  }, [readFileAsText, loadTemplate]);

  // Create text layer from pasted text
  const createTextFromPaste = useCallback((text: string, x: number, y: number) => {
    const layer: TextLayer = {
      id: nanoid(),
      type: 'text',
      name: text.slice(0, 20) + (text.length > 20 ? '...' : ''),
      position: createAbsolutePosition(x, y),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        text,
        fontSize: textDefaults.fontSize,
        fontFamily: textDefaults.fontFamily,
        fontWeight: textDefaults.fontWeight,
        color: textDefaults.color,
        textAlign: textDefaults.textAlign,
      },
    };

    addLayer(layer);
    selectLayer(layer.id);
  }, [textDefaults, addLayer, selectLayer]);

  // Handle file drop
  const handleFileDrop = useCallback(async (
    files: FileList,
    dropX: number,
    dropY: number
  ) => {
    for (const file of Array.from(files)) {
      const fileType = getFileType(file);

      switch (fileType) {
        case 'image':
          await createImageFromFile(file, dropX, dropY);
          break;
        case 'video':
          await createVideoFromFile(file, dropX, dropY);
          break;
        case 'audio':
          await createAudioFromFile(file);
          break;
        case 'lottie':
          await createLottieFromFile(file, dropX, dropY);
          break;
        case 'rive':
          await createRiveFromFile(file, dropX, dropY);
          break;
        case 'slate':
          await loadSlateFile(file);
          break;
        default:
          console.warn('Unsupported file type:', file.name);
      }
    }
  }, [createImageFromFile, createVideoFromFile, createAudioFromFile, createLottieFromFile, createRiveFromFile, loadSlateFile]);

  // Drag event handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!enabled) return;

    const items = e.dataTransfer.items;
    let fileType: DropzoneState['draggedFileType'] = 'unknown';

    if (items.length > 0) {
      const item = items[0];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          fileType = getFileType(file);
        }
      }
    }

    setDropzoneState({
      isDraggingOver: true,
      draggedFileType: fileType,
    });
  }, [enabled]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only reset if we're leaving the container (not entering a child)
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!e.currentTarget.contains(relatedTarget)) {
      setDropzoneState({
        isDraggingOver: false,
        draggedFileType: null,
      });
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setDropzoneState({
      isDraggingOver: false,
      draggedFileType: null,
    });

    if (!enabled) return;

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    // Calculate drop position relative to canvas center
    const canvas = document.getElementById('ad-canvas');
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const zoom = rect.width / canvasWidth;

    const dropX = (e.clientX - rect.left) / zoom;
    const dropY = (e.clientY - rect.top) / zoom;

    handleFileDrop(files, dropX, dropY);
  }, [enabled, canvasWidth, handleFileDrop]);

  // Paste handler
  useEffect(() => {
    if (!enabled) return;

    const handlePaste = async (e: ClipboardEvent) => {
      // Don't handle if typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      // Check for files (images)
      const files = clipboardData.files;
      if (files.length > 0) {
        e.preventDefault();
        // Drop at center of canvas
        const centerX = canvasWidth / 2;
        const centerY = canvasHeight / 2;
        await handleFileDrop(files, centerX, centerY);
        return;
      }

      // Check for text
      const text = clipboardData.getData('text/plain');
      if (text && text.trim()) {
        e.preventDefault();
        // Create text at center of canvas
        const centerX = canvasWidth / 2 - 100; // Offset for text width
        const centerY = canvasHeight / 2;
        createTextFromPaste(text.trim(), centerX, centerY);
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [enabled, canvasWidth, canvasHeight, handleFileDrop, createTextFromPaste]);

  return {
    dropzoneState,
    dropzoneProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}
